import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { chatRealtimeManager } from '@/lib/chatRealtimeManager';

const BACKGROUND_PAUSE_DELAY = 5000; // 5s debounce before pausing in background

/**
 * Controls ChatRealtimeManager lifecycle based on:
 * 1. Route: channels only active when user is on /chat
 * 2. Visibility: channels paused after 5s in background, resumed on foreground (if on /chat)
 *
 * Mount once in AppContent (inside BrowserRouter).
 * notifications-changes channel is NOT affected — it's managed separately by NotificationCenter.
 */
export function useRealtimeLifecycle() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const previousPathRef = useRef<string>(location.pathname);
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isBackgroundPausedRef = useRef(false);

  const isOnChat = (path: string) => path.startsWith('/chat');

  // Route-based lifecycle
  useEffect(() => {
    const currentPath = location.pathname;
    const wasInChat = isOnChat(previousPathRef.current);
    const nowInChat = isOnChat(currentPath);

    if (nowInChat && !wasInChat) {
      // Entering /chat: resume channels + refetch stale data
      chatRealtimeManager.resume();
      isBackgroundPausedRef.current = false;
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] });
    } else if (!nowInChat && wasInChat) {
      // Leaving /chat: pause channels
      chatRealtimeManager.pause();
    }

    previousPathRef.current = currentPath;
  }, [location.pathname, queryClient]);

  // Start paused if not on /chat at mount
  useEffect(() => {
    if (!isOnChat(location.pathname)) {
      chatRealtimeManager.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Visibility-based lifecycle
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab going to background — debounce 5s
        pauseTimeoutRef.current = setTimeout(() => {
          chatRealtimeManager.pause();
          isBackgroundPausedRef.current = true;
        }, BACKGROUND_PAUSE_DELAY);
      } else {
        // Tab coming to foreground — cancel pending pause
        if (pauseTimeoutRef.current) {
          clearTimeout(pauseTimeoutRef.current);
          pauseTimeoutRef.current = null;
        }

        // Only resume if we actually paused AND user is on /chat
        if (isBackgroundPausedRef.current && isOnChat(previousPathRef.current)) {
          chatRealtimeManager.resume();
          queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
        }
        isBackgroundPausedRef.current = false;

        // Always refresh notifications and unread badge on foreground
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['unread-badge'] });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, [queryClient]);
}
